/**
 * Utility functions for clearing browser storage (localStorage, sessionStorage, cookies)
 * Used during logout to ensure complete cleanup of user data
 */

import { CONSENT_STORAGE_KEY } from '@/entities/consent/model/constants'

/**
 * localStorage keys that must survive logout. Cookie consent is a
 * per-device choice (GDPR Art. 7) — logging out does not withdraw it.
 */
const PRESERVED_LOCAL_STORAGE_KEYS: readonly string[] = [CONSENT_STORAGE_KEY]

/**
 * Clears all cookies from the current domain and path
 * This function deletes every cookie accessible to JavaScript
 */
export function clearAllCookies(): void {
  if (typeof document === 'undefined') {
    return
  }

  // Get all cookies
  const cookies = document.cookie.split(';')

  // Delete each cookie
  for (const cookie of cookies) {
    const eqPos = cookie.indexOf('=')
    const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim()

    // Delete cookie for current path
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`

    // Also try to delete for root path
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`

    // Try to delete for current domain
    const domain = window.location.hostname
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`

    // Try to delete for parent domain (e.g., .example.com)
    const domainParts = domain.split('.')
    if (domainParts.length > 1) {
      const parentDomain = domainParts.slice(-2).join('.')
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${parentDomain}`
    }
  }
}

/**
 * Clears all localStorage data, preserving keys listed in
 * {@link PRESERVED_LOCAL_STORAGE_KEYS} (e.g. cookie-consent record).
 * Returns true if successful, false if localStorage is not available
 */
export function clearLocalStorage(): boolean {
  if (typeof localStorage === 'undefined') {
    return false
  }

  try {
    const preserved = new Map<string, string>()
    for (const key of PRESERVED_LOCAL_STORAGE_KEYS) {
      const value = localStorage.getItem(key)
      if (value !== null)
        preserved.set(key, value)
    }

    localStorage.clear()

    for (const [key, value] of preserved)
      localStorage.setItem(key, value)

    return true
  }
  catch (error) {
    console.error('Error clearing localStorage:', error)
    return false
  }
}

/**
 * Clears all sessionStorage data
 * Returns true if successful, false if sessionStorage is not available
 */
export function clearSessionStorage(): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false
  }

  try {
    sessionStorage.clear()
    return true
  }
  catch (error) {
    console.error('Error clearing sessionStorage:', error)
    return false
  }
}

/**
 * Comprehensive cleanup function that clears all browser storage
 * This should be called during user logout to ensure complete data cleanup
 *
 * @returns An object indicating what was successfully cleared
 */
export function clearAllStorageOnLogout(): {
  cookies: boolean
  localStorage: boolean
  sessionStorage: boolean
} {
  const result = {
    cookies: false,
    localStorage: false,
    sessionStorage: false,
  }

  try {
    // Clear all cookies
    clearAllCookies()
    result.cookies = true
  }
  catch (error) {
    console.error('Error clearing cookies:', error)
  }

  try {
    // Clear localStorage
    result.localStorage = clearLocalStorage()
  }
  catch (error) {
    console.error('Error clearing localStorage:', error)
  }

  try {
    // Clear sessionStorage
    result.sessionStorage = clearSessionStorage()
  }
  catch (error) {
    console.error('Error clearing sessionStorage:', error)
  }

  return result
}
